"use client"

import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/free-mode"
import { FreeMode, Navigation } from "swiper/modules"
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react"
import { TImagesProps } from "@/app/types/types"
import CircleIconButton from "../CircleIconButton"
import OneDirectionArrow from "../icons/OneDirectionArrow"
import { useContext, useRef, useState } from "react"
import Image from "next/image"
import ImageWithLoader from "../imageWithLoader/ImageWithLoader"
import FullWidthImage from "../icons/FullWidthImage"
import CloseWidthImage from "../icons/CloseWidthImage"
import { screenSizeContext } from "@/app/store/context/screenSizesContext"
import Popup from "../Popup/Popup"
import { usePopupContext } from "@/app/store/context/popupContext"
import dynamic from 'next/dynamic';
import VideoIcon from "../icons/VideoIcon"

const ReactPlayerComp = dynamic(() => import('react-player'), {
	ssr: false,
});

const GeneralSwiper = ({
	images,
	slidesPerView,
	spaceBetween,
	onSlideChange,
	isMultiple,
	navigationIndex,
	enableFullScreenSwiper = false, // New prop to enable full-screen Swiper
	customImgclass,
	FullWidth,
	ImgHeight,
	swiperClass,
	useOuterPopup = false,
	insideAmenitiesDetailsPage,
	optimizedImage = false,
}: TImagesProps) => {
	const swiperRef = useRef<SwiperRef>(null)
	const fullScreenSwiperRef = useRef<SwiperRef>(null)
	const { openPopup } = usePopupContext()
	const [isFirstSlide, setIsFirstSlide] = useState(true)
	const [isLastSlide, setIsLastSlide] = useState(false)
	const [selectedImage, setSelectedImage] = useState<string | null>(null)
	const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0) // Track selected image index
	const [fullScreenSwiper, setfullScreenSwiper] = useState(false);
	const { isIpad, isMobile } = useContext(screenSizeContext)

	const handleSlideChange = () => {
		if (swiperRef?.current) {
			setIsFirstSlide(swiperRef?.current?.swiper?.isBeginning)
			setIsLastSlide(swiperRef?.current?.swiper?.isEnd)
		}

		if (fullScreenSwiperRef?.current) {
			setIsFirstSlide(fullScreenSwiperRef?.current?.swiper?.isBeginning)
			setIsLastSlide(fullScreenSwiperRef?.current.swiper?.isEnd)
		}
	}
	const openFullScreen = (src: string, index: number) => {
		setSelectedImage(src)
		setSelectedImageIndex(index)
		if (enableFullScreenSwiper && !useOuterPopup) {
			setfullScreenSwiper(true)
		}

		if (useOuterPopup) {
			openPopup(images, src, index)
		}

		document
			.querySelector(".popup.popup_half .popup_content")
			?.classList.add("overflow-hidden")
	}

	const closeFullScreen = () => {
		setSelectedImage(null)
		setSelectedImageIndex(0) // Reset the selected image index
		document
			.querySelector(".popup.popup_half .popup_content")
			?.classList.remove("overflow-hidden")
	}

	return (
		<div className="swiper_wrapper relative h-full">
			<Swiper
				ref={swiperRef}
				modules={[Navigation, FreeMode]}
				spaceBetween={8}
				slidesPerView={slidesPerView}
				onSlideChange={handleSlideChange}
				navigation={{
					nextEl: `.swiper-next${isMultiple ? `-${navigationIndex}` : ""}`,
					prevEl: `.swiper-prev${isMultiple ? `-${navigationIndex}` : ""}`,
				}}
				freeMode
			>
				{images?.map((item, index) => (
					item &&
					<SwiperSlide key={index}>
						<div className="relative">
							<div className={`swiper_image_container relative w-full ${ImgHeight ? ImgHeight : "h-[20rem] md:h-[27rem]"}`} >
								{
									item?.type !== "video" && (
										optimizedImage ? (
											<ImageWithLoader
												src={item?.src}
												alt={item?.alt}
												sizeVariant="fullWidth"
												quality={90}
											/>
										) : (
											<Image
												fill
												src={item?.src}
												alt={item?.alt}
												className="object-cover"
												sizes="(max-width: 768px) min(100vw, 640px), 100vw"
												quality={90}
											/>
										)
									)
								}

								{
									item?.type === "video" && (
										<div className="video-cover !h-full relative"
										>
											{
												item?.videoPoster ? (
													<Image 
														fill 
														src={item?.videoPoster} 
														alt={item?.alt}
														className="object-cover"
														sizes="(max-width: 768px) min(100vw, 640px), 100vw"
														quality={90} 
													/>) 
													: (<ReactPlayerComp
														url={item?.src}
														playing={false}
														loop
														width="100%"
														height="100%"
														controls={false}
														muted
														playsinline
														className="pointer-events-none"
													/>)
											}

											<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white z-50 cursor-pointer"
												onClick={() => openFullScreen(item?.src, index)}
											>
												<VideoIcon />
											</div>
										</div>
									)
								}

							</div>
							{FullWidth && (
								<div
									className={` absolute right-3 bottom-3 p-4 z-[9999] cursor-pointer ${insideAmenitiesDetailsPage && images?.length < 2 ? "hidden" : ""}`}
									onClick={() => openFullScreen(item?.src, index)}
									style={{
										borderRadius: "8.125rem",
										border: "1px solid rgba(252, 252, 252, 0)",
										// background: "rgba(134, 134, 134, 0.5)",
										background: "rgba(134, 134, 134, 0.50)",
										boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.04)",
										backdropFilter: "blur(32px)",
									}}
								>
									<FullWidthImage />
								</div>
							)}
						</div>
					</SwiperSlide>
				))}
			</Swiper>
			{!isIpad &&
				!isMobile && (
					<div className="swiper_btns_container absolute w-full flex justify-between top-[50%] translate-y[-50%] z-[1] px-4 pointer-events-none">
						<CircleIconButton
							style="white"
							icon={<OneDirectionArrow color="white" />}
							customClass={`scale-x-[-1] !bg-[rgba(134,134,134,0.5)] swiper-prev${isMultiple ? `-${navigationIndex}` : ""} ${isFirstSlide
								? "fade-out duration-500 opacity-0"
								: "fade-in duration-500 opacity-100"
								}
								pointer-events-auto
								`}
						/>
						<CircleIconButton
							style="white"
							icon={<OneDirectionArrow color="white" />}
							customClass={`!bg-[rgba(134,134,134,0.5)] swiper-next${isMultiple ? `-${navigationIndex}` : ""} ${isLastSlide
								? "fade-out duration-500 opacity-0"
								: "fade-in duration-500 opacity-100"
								}  ${images?.length === 1 ? "hidden" : ""}
								pointer-events-auto
								`}
						/>
					</div>
				)}

			{selectedImage && !enableFullScreenSwiper && (
				<div
					className="fixed inset-0 bg-white flex items-center justify-center z-50"
					onClick={closeFullScreen}
				>
					<div className="relative w-[90%] md:w-[100%] h-[50%] lg:h-[100%]">
						{optimizedImage ? (
							<ImageWithLoader
								src={selectedImage}
								alt="Fullscreen Image"
								sizeVariant="fullWidth"
								contain
								quality={90}
							/>
						) : (
							<Image
								src={selectedImage}
								alt="Fullscreen Image"
								fill
								className="object-cover"
								sizes="(max-width: 768px) min(100vw, 640px), 100vw"
								quality={90}
							/>
						)}
					</div>
					<div
						className="absolute right-5 bottom-3 p-4 cursor-pointer "
						style={{
							borderRadius: "8.125rem",
							border: "1px solid rgba(252, 252, 252, 0)",
							background: "rgba(134, 134, 134, 0.5)",
							boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.04)",
							backdropFilter: "blur(32px)",
						}}
					>
						<CloseWidthImage />
					</div>
				</div>
			)}

			{
				selectedImage && enableFullScreenSwiper && (
					// Full-screen Swiper
					<Popup
						customClassWrapper="popup_half z-[9999] overflow-hidden"
						setIsOpen={setfullScreenSwiper}
						isOpen={fullScreenSwiper}
					>
						<div className={`full-screen-swiper-wrapper relative w-full h-full py-[5.25rem] ${swiperClass} `}>
							<Swiper
								ref={fullScreenSwiperRef}
								initialSlide={selectedImageIndex} // Start from the selected image
								modules={[Navigation, FreeMode]}
								spaceBetween={0}
								slidesPerView={1}
								onSlideChange={handleSlideChange}
								className="w-full h-full"
								navigation={{
									nextEl: ".swiper-full-next",
									prevEl: ".swiper-full-prev",
								}}
								freeMode
							>
								{images?.map((item, index) => (
									<SwiperSlide key={index}>
										<div className="relative w-full h-full">
											{optimizedImage ? (
												<ImageWithLoader
													src={item.src}
													alt={item.alt}
													sizeVariant="fullWidth"
													contain
													quality={90}
												/>
											) : (
												<Image
													src={item.src}
													alt={item.alt}
													fill
													className="object-cover"
													sizes="(max-width: 768px) min(100vw, 640px), 100vw"
													quality={90}
												/>
											)}
										</div>
									</SwiperSlide>
								))}
							</Swiper>
							<div className="swiper_btns_container absolute w-full flex justify-between top-[50%] translate-y[-50%] z-[1] px-4">
								<CircleIconButton
									style="white"
									icon={<OneDirectionArrow color="white" />}
									customClass={`scale-x-[-1] swiper-full-prev ${isFirstSlide
										? "fade-out duration-500 opacity-0"
										: "fade-in duration-500 opacity-100"
										}`}
								/>
								<CircleIconButton
									style="white"
									icon={<OneDirectionArrow color="white" />}
									customClass={`swiper-full-next ${isLastSlide
										? "fade-out duration-500 opacity-0"
										: "fade-in duration-500 opacity-100"
										}`}
								/>
							</div>
							<div
								className="absolute right-4 bottom-4 p-4 cursor-pointer "
								style={{
									borderRadius: "8.125rem",
									border: "1px solid rgba(252, 252, 252, 0)",
									background: "rgba(134, 134, 134, 0.5)",
									boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.04)",
									backdropFilter: "blur(32px)",
								}}
								onClick={() => setfullScreenSwiper(false)}
							>
								<CloseWidthImage />
							</div>
						</div>

					</Popup>
				)}
		</div>
	)
}

export default GeneralSwiper